"use client";

import { useMemo, useState } from "react";
import {
  Shield,
  Bell,
  CheckCircle2,
  AlertTriangle,
  CheckCheck,
  ArrowLeft,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSystemNotifications } from "@/hooks";
import Loader from "@/components/loader/Loader";
import { useMutation } from "@tanstack/react-query";
import { makeRequest } from "@/utils/makeRequest";
import { queryClient } from "@/services/queryClient";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/store/hooks";
import { ErrorState } from "@/components/ErrorState";

function groupByDate(items: any[] = []) {
  const groups: Record<string, any[]> = {
    Today: [],
    Yesterday: [],
    Earlier: [],
  };

  const now = Date.now();

  for (const n of items) {
    const diff = now - new Date(n.createdAt).getTime();

    if (diff < 86400000) groups.Today.push(n);
    else if (diff < 172800000) groups.Yesterday.push(n);
    else groups.Earlier.push(n);
  }

  return groups;
}

export default function AdminNotifications() {
  const [query, setQuery] = useState("");

  const { user } = useAppSelector((state) => state.auth);

  const userId = user?.id || "";

  const navigate = useNavigate();

  // pagination state
  const [page, setPage] = useState(1);
  const limit = 10;

  const {
    data: notificationsResponse,
    isLoading,
    isError,
    refetch,
  } = useSystemNotifications({ page, limit });

  // normalize API shape safely
  const notifications = useMemo(() => {
    return notificationsResponse?.data ?? [];
  }, [notificationsResponse]);

  const meta = notificationsResponse?.meta;

  // filtering
  const filtered = useMemo(() => {
    return notifications.filter((n: any) =>
      `${n.title} ${n.description}`.toLowerCase().includes(query.toLowerCase()),
    );
  }, [notifications, query]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  const iconMap: any = {
    SECURITY: <Shield className="w-4 h-4 text-red-500" />,
    TRADE: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
    SYSTEM: <Bell className="w-4 h-4 text-blue-500" />,
    DEFAULT: <AlertTriangle className="w-4 h-4 text-orange-500" />,
  };

  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      return makeRequest.put(`/notification/${id}/read`);
    },

    onMutate: async (id: string) => {
      await queryClient.cancelQueries({
        queryKey: ["notifications:me", page, limit, userId],
      });

      const previous = queryClient.getQueryData([
        "notifications:me",
        page,
        limit,
        userId,
      ]);

      queryClient.setQueryData(
        ["notifications:me", page, limit, userId],
        (old: any) => {
          if (!old?.data) return old;

          return {
            ...old,
            data: old.data.map((n: any) => {
              if (n.id !== id) return n;

              return {
                ...n,
                recipients: n.recipients.map((r: any) => ({
                  ...r,
                  isRead: true,
                  readAt: new Date().toISOString(),
                })),
              };
            }),
          };
        },
      );

      return { previous };
    },

    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(
          ["notifications:me", page, limit, userId],
          ctx.previous,
        );
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications:me", page, limit, userId],
      });
    },
  });

  const markAsRead = (id: string) => {
    markAsReadMutation.mutate(id);
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications
      .filter((n: any) => n.recipients?.some((r: any) => !r.isRead))
      .map((n: any) => n.id);

    for (const id of unreadIds) {
      await markAsReadMutation.mutateAsync(id);
    }
  };

  const unreadCount = notifications.filter((n: any) =>
    n.recipients?.some((r: any) => !r.isRead),
  ).length;

  if (isLoading) return <Loader type="bounce" />;

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background lg:px-6 mt-4">
      <Button
        className="hidden lg:flex"
        variant="ghost"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>
      {/* Header */}
      <div className="flex justify-between items-start">
        {/* <div>
          <h1 className="text-xl font-semibold tracking-tight">
            Notification Center
          </h1>
          <p className="text-sm text-muted-foreground">
            Security events, transactions, system updates
          </p>
        </div> */}

        <Button
          variant="outline"
          size="sm"
          onClick={markAllAsRead}
          disabled={unreadCount === 0}
        >
          Mark all as read
        </Button>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <Input
          placeholder="Search events, trades, alerts..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/* Timeline Groups */}
      <div className="space-y-8">
        {Object.entries(grouped).map(([label, items]) =>
          items.length ? (
            <div key={label}>
              <h2 className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
                {label}
              </h2>

              <div className="space-y-1 border-l pl-3">
                {items.map((n: any) => {
                  const isRead = n.recipients?.some((r: any) => r.isRead);

                  return (
                    <div
                      key={n.id}
                      className={`group relative flex gap-4 p-4 rounded-xl transition hover:bg-muted/40 ${
                        !isRead ? "bg-muted/20" : ""
                      }`}
                    >
                      <div className="relative flex flex-col items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center border ${
                            n.notifType === "SECURITY"
                              ? "border-red-200 bg-red-50"
                              : n.notifType === "TRADE"
                                ? "border-green-200 bg-green-50"
                                : "border-blue-200 bg-blue-50"
                          }`}
                        >
                          {iconMap[n.notifType] || iconMap.DEFAULT}
                        </div>

                        <div className="flex-1 w-px bg-border mt-2" />
                      </div>

                      <div className="flex-1">
                        <div className="flex justify-between items-start gap-3">
                          <div>
                            <h3 className="font-medium text-sm flex items-center gap-2">
                              {n.title}
                              {!isRead && (
                                <span className="w-2 h-2 rounded-full bg-blue-500" />
                              )}
                            </h3>

                            <p className="text-xs text-muted-foreground mt-1">
                              {n.description}
                            </p>

                            <div className="flex gap-3 mt-2 text-[11px] text-muted-foreground">
                              <span>
                                {new Date(n.createdAt).toLocaleString()}
                              </span>
                              <span>•</span>
                              <span>{n.notifType}</span>
                            </div>
                          </div>

                          {!isRead && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => markAsRead(n.id)}
                            >
                              <CheckCheck className="w-4 h-4 mr-1" />
                              Read
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null,
        )}
      </div>

      {/* Pagination */}
      {meta && (
        <div className="flex justify-between items-center pt-4">
          <Button
            variant="outline"
            disabled={!meta.hasPrevPage}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>

          <p className="text-xs text-muted-foreground">
            Page {meta.page} of {meta.totalPages}
          </p>

          <Button
            variant="outline"
            disabled={!meta.hasNextPage}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
